import { useEffect, useRef, useState } from 'react';

interface ChatComposerProps {
  /** Sends the message; resolves true when accepted (the box then clears). */
  onSend: (text: string) => Promise<boolean>;
  disabled?: boolean;
  autoFocus?: boolean;
  placeholder?: string;
}

const isMac =
  typeof navigator !== 'undefined' && /mac|iphone|ipad|ipod/i.test(navigator.platform ?? '');

/**
 * The chat message box: textarea + Send, ⌘/Ctrl+Enter on pointer devices
 * (touch devices get no shortcut copy — design review D4). Keeps the typed
 * text on failure; clears only when the send is accepted.
 */
export function ChatComposer({ onSend, disabled, autoFocus, placeholder }: ChatComposerProps) {
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);

  useEffect(() => {
    if (autoFocus) textareaRef.current?.focus();
    // Mount-only: the sheet focuses its textarea once on open.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Keyboard-shortcut hints only make sense on hover/pointer devices; computed
  // at render time so per-test matchMedia mocks are read.
  const canHover =
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(hover: hover)').matches;

  const submit = async (): Promise<void> => {
    if (disabled || sending || !text.trim()) return;
    setSending(true);
    try {
      const accepted = await onSend(text);
      if (accepted) setText('');
    } finally {
      setSending(false);
    }
  };

  const handleSubmit = (e: React.FormEvent): void => {
    e.preventDefault();
    void submit();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>): void => {
    if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
      e.preventDefault();
      void submit();
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <textarea
        ref={textareaRef}
        rows={2}
        aria-label="Message me"
        placeholder={placeholder ?? 'Write what’s happening, or ask about your notes…'}
        value={text}
        readOnly={sending}
        enterKeyHint="send"
        onChange={(e) => setText(e.target.value)}
        onKeyDown={handleKeyDown}
        onFocus={(e) => e.currentTarget.scrollIntoView({ block: 'nearest' })}
      />
      <div className="mt-3 flex items-center justify-end gap-3 flex-wrap">
        {canHover && (
          <p className="help mr-auto">{isMac ? '⌘+Enter to send' : 'Ctrl+Enter to send'}</p>
        )}
        <button
          type="submit"
          data-testid="alma-submit"
          className="btn btn-primary"
          disabled={disabled || sending || !text.trim()}
        >
          Send
        </button>
      </div>
    </form>
  );
}
